<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Team</div>
                <div class="page-bar-main">Celebrations</div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view celebrations' : 'hide'">
            <!-- Team events (routes/teamevents.ts), made from The Forge -->
            <template v-if="events.length || isTeamHubAdmin">
                <div class="cel-section-head">
                    <div class="cel-eyebrow">Team Events</div>
                    <forge-page-button :kinds="['event']" label="Plan an event with AI" @applied="loadEvents()" />
                </div>
                <div class="cel-upcoming cel-events">
                    <div v-if="!events.length" class="cel-empty">No team events coming up.</div>
                    <div v-for="ev in events" :key="ev.ID" class="cel-upcoming-row">
                        <div class="cel-bigdate">
                            <div class="cel-bigdate-mon">{{ $store.state.monthShortStrings[Number(ev.Date.slice(5, 7))].toUpperCase() }}</div>
                            <div class="cel-bigdate-day">{{ Number(ev.Date.slice(8, 10)) }}</div>
                        </div>
                        <div class="cel-dot cel-dot-event"></div>
                        <div class="cel-upcoming-main">
                            <div class="cel-upcoming-name">{{ ev.Name }}</div>
                            <div class="cel-upcoming-sub">{{ eventWhen(ev) }}</div>
                            <div v-if="ev.Description" class="cel-event-desc">{{ ev.Description }}</div>
                        </div>
                        <div class="cel-upcoming-days">
                            {{ daysAwayText(daysUntil(ev.Date)) }}
                            <a v-if="isTeamHubAdmin" class="cel-remove" @click.prevent="removeEvent(ev)">{{ removing == ev.ID ? 'Confirm remove' : 'Remove' }}</a>
                        </div>
                    </div>
                </div>
            </template>

            <div class="cel-section-head" :style="events.length || isTeamHubAdmin ? 'margin-top:35px' : ''">
                <div class="cel-eyebrow">Coming Up</div>
                <div class="cel-eyebrow">Next 60 Days</div>
            </div>
            <div class="cel-upcoming">
                <div v-if="!comingUp.length" class="cel-empty">No celebrations in the next 60 days.</div>
                <div v-for="(c, i) in comingUp" :key="i" class="cel-upcoming-row">
                    <div class="cel-bigdate">
                        <div class="cel-bigdate-mon">{{ $store.state.monthShortStrings[c.month].toUpperCase() }}</div>
                        <div class="cel-bigdate-day">{{ c.day }}</div>
                    </div>
                    <div class="cel-dot" :style="{ background: typeColor(c.celName) }"></div>
                    <div class="cel-upcoming-main">
                        <div class="cel-upcoming-name">{{ c.emp }}</div>
                        <div class="cel-upcoming-sub">{{ comingUpText(c) }}</div>
                    </div>
                    <div class="cel-upcoming-days">{{ daysAwayText(c.numDays) }}</div>
                </div>
            </div>

            <div class="cel-section-head" style="margin-top:35px">
                <div class="cel-eyebrow">All Year</div>
                <div class="cel-legend">
                    <span v-for="(t, i) in celTypes" :key="i" class="cel-legend-item">
                        <span class="cel-dot" :style="{ background: typeColor(t) }"></span>{{ t }}
                    </span>
                </div>
            </div>
            <div v-if="!cels.length" class="cel-empty">No celebration fields have been configured yet.</div>
            <div class="cel-grid">
                <div v-for="group in monthsGrouped" :key="group.month" class="cel-month-card">
                    <div class="cel-month-name">{{ group.name }}</div>
                    <div class="cel-month-list">
                        <div v-for="(c, i) in group.items" :key="i" class="cel-month-row">
                            <span class="cel-dot" :style="{ background: typeColor(c.celName) }"></span>
                            <div class="cel-month-name-cell">{{ c.emp }}</div>
                            <div class="cel-month-value">{{ gridValue(c) }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import ForgePageButton from '@/components/ForgePageButton.vue';

export default defineComponent({
    name: 'CelebrationsView',
    components: {
        ForgePageButton
    },
    data() {
        return {
            celFields: [] as any,
            employeesAll: [] as any,
            cels: [] as any,
            events: [] as any[],
            removing: null as any,
            typeColors: [ '#E63946', '#3D77D6', '#F4A62B', '#1E9E5A', '#E85D9B', '#8E44AD', '#2C7DA0' ]
        }
    },
    computed: {
        isTeamHubAdmin(): boolean {
            return this.$store.state.user.teamHubRole == 'Admin';
        },
        celTypes(): string[] {
            const seen: string[] = [];
            for (const c of this.cels) {
                if (!seen.includes(c.celName)) {
                    seen.push(c.celName);
                }
            }
            return seen;
        },
        comingUp(): any[] {
            return this.cels.filter((c: any) => c.numDays <= 60).sort((a: any, b: any) => a.numDays - b.numDays);
        },
        monthsGrouped(): any[] {
            const groups = [];
            for (let m = 1; m <= 12; m++) {
                const items = this.cels.filter((c: any) => c.month === m).sort((a: any, b: any) => a.day - b.day);
                if (items.length) {
                    groups.push({ month: m, name: this.$store.state.monthStrings[m], items });
                }
            }
            return groups;
        }
    },
    async created() {
        this.loadEvents();
        await axios.get(`${this.$store.state.apiUrl}/api/employees/limitedinfo`)
            .then(resp => {
                this.employeesAll = resp.data;
            })
            .catch(e => {
                console.log(e);
            })

        while(!this.$store.state.employeeFieldsLoaded) {
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        this.celFields = JSON.parse(JSON.stringify(this.$store.state.employeeFields)).filter((f: any) => {
            return f.celebrationDate && f.type == 'date';
        })

        const now = new Date();
        const currentZeroMS = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

        for(const emp of this.employeesAll) {
            for(const celField of this.celFields) {
                const empField = emp.AdditionalFields.find((f: any) => {
                    return f.id == celField.id;
                });
                if(empField?.value) {
                    try {
                        const date = new Date(empField.value);
                        const month = date.getUTCMonth();
                        const day = date.getUTCDate();
                        const year = date.getUTCFullYear();

                        let targetYear = now.getFullYear();
                        let targetDate = new Date(targetYear, month, day);

                        // Date has already past this year
                        if(targetDate.getTime() < currentZeroMS) {
                            targetYear = now.getFullYear() + 1;
                            targetDate.setFullYear(targetYear);
                        }

                        const timeDiff = targetDate.getTime() - currentZeroMS;
                        const numDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
                        const numYears = targetYear - year;
                        const celName = celField.celebrationName || celField.name;

                        // Skip anniversaries that haven't reached a full year yet (e.g. hired this year)
                        if(numYears < 1 && !this.isBirthdayType(celName)) {
                            continue;
                        }

                        this.cels.push({
                            emp: emp.Name,
                            celName: celName,
                            celID: celField.id,
                            ms: date.getTime(),
                            numDays: numDays,
                            numYears: numYears,
                            dateString: `${this.$store.state.monthShortStrings[month+1]} ${day}, ${year}`,
                            monthDay: `${this.$store.state.monthShortStrings[month+1]} ${day}`,
                            month: month + 1,
                            day: day
                        })
                    } catch(e) {
                        console.log(e);
                    }
                }
            }
        }

        this.$store.state.routeLoaded = true;
    },
    methods: {
        async loadEvents() {
            await axios.get(`${this.$store.state.apiUrl}/api/teamevents`)
                .then(resp => {
                    this.events = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        daysUntil(date: string) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return Math.round((new Date(date + 'T00:00:00').getTime() - today.getTime()) / 86400000);
        },
        // "2:00 PM · 2 hours · Cincinnati" (the start time as entered, in the event's own time zone)
        eventWhen(ev: any) {
            const parts = [];
            if (ev.StartTime) {
                const [ h, m ] = ev.StartTime.split(':').map(Number);
                parts.push(new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }));
            }
            if (ev.DurationMinutes) {
                parts.push(ev.DurationMinutes < 60 ? `${ev.DurationMinutes} min` : `${+(ev.DurationMinutes / 60).toFixed(1)} hour${ev.DurationMinutes == 60 ? '' : 's'}`);
            }
            parts.push(ev.Location || 'Everyone');
            return parts.join(' · ');
        },
        async removeEvent(ev: any) {
            if (this.removing != ev.ID) {
                this.removing = ev.ID;
                return;
            }
            await axios.delete(`${this.$store.state.apiUrl}/api/teamevents/${ev.ID}`)
                .then(() => {
                    this.events = this.events.filter((e: any) => e.ID != ev.ID);
                })
                .catch(e => {
                    console.log(e);
                    alert('The event could not be removed');
                })
            this.removing = null;
        },
        isBirthdayType(celName: string) {
            return celName.toLowerCase().includes('birthday');
        },
        typeColor(celName: string) {
            const idx = this.celTypes.indexOf(celName);
            return this.typeColors[idx % this.typeColors.length];
        },
        daysAwayText(numDays: number) {
            if (numDays <= 0) {
                return 'Today';
            }
            return `in ${numDays} day${numDays === 1 ? '' : 's'}`;
        },
        // Birthday ages (numYears = age at the next occurrence) are only shown to admin users
        comingUpText(c: any) {
            if (this.isBirthdayType(c.celName)) {
                return this.$store.state.user.isAdmin ? `${c.celName} · turning ${c.numYears}` : c.celName;
            }
            return `${c.numYears}-year ${c.celName.toLowerCase()}`;
        },
        gridValue(c: any) {
            if (this.isBirthdayType(c.celName)) {
                return this.$store.state.user.isAdmin ? `${c.monthDay} · ${c.numYears}` : c.monthDay;
            }
            return `${c.monthDay} · ${c.numYears} yrs`;
        }
    }
})
</script>

<style scoped>
.cel-section-head { display: flex; align-items: center; justify-content: space-between; margin: 10px 0; flex-wrap: wrap; gap: 10px; }
.cel-eyebrow { font-family: var(--head-font); font-weight: 700; font-size: 12px; letter-spacing: .05em; text-transform: uppercase; color: #93979A; }
.cel-legend { display: flex; flex-wrap: wrap; gap: 18px; }
.cel-legend-item { display: flex; align-items: center; font-family: var(--head-font); font-weight: 700; font-size: 12px; letter-spacing: .05em; text-transform: uppercase; color: #93979A; }
.cel-legend-item .cel-dot { margin-right: 8px; }

.cel-dot { width: 10px; height: 10px; border-radius: 3px; flex-shrink: 0; }

.cel-upcoming { max-height: 300px; overflow: auto; display: flex; flex-direction: column; gap: 10px; }
.cel-upcoming-row { display: flex; align-items: center; gap: 16px; background: #fff; border-radius: 10px; padding: 14px 20px; margin-right: 5px; }
.cel-bigdate { min-width: 44px; text-align: center; }
.cel-bigdate-mon { font-size: 11px; font-weight: 700; letter-spacing: .04em; color: #93979A; }
.cel-bigdate-day { font-size: 20px; font-weight: 800; color: var(--dark-gray); line-height: 1.2; }
.cel-upcoming-main { flex: 1; min-width: 0; }
.cel-upcoming-name { font-weight: 700; color: var(--dark-gray); }
.cel-upcoming-sub { font-size: 13px; color: #74787C; margin-top: 2px; }
.cel-upcoming-days { font-size: 13px; color: #93979A; white-space: nowrap; }

.cel-empty { color: #93979A; font-size: 14px; padding: 10px 0; }
.cel-link { text-decoration: none; color: var(--primary-color); }
.cel-dot-event { background: #F4A62B; }
.cel-events { max-height: none; }
.cel-event-desc { font-size: 13px; color: #55595C; margin-top: 4px; }
.cel-remove { display: block; margin-top: 4px; font-size: 12px; color: #b91c1c; cursor: pointer; text-align: right; }

.cel-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; }
.cel-month-card { background: #fff; border-radius: 10px; padding: 20px 10px 20px 20px; }
.cel-month-name { font-family: var(--head-font); font-weight: 700; color: var(--dark-gray); margin-bottom: 10px; }
.cel-month-row { margin-right: 10px;display: flex; align-items: center; gap: 10px; padding: 6px 0; }
.cel-month-name-cell { flex: 1; min-width: 0; font-size: 14px; color: var(--dark-gray); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cel-month-value { font-size: 13px; color: #93979A; white-space: nowrap; }
.cel-month-list { max-height: 300px; overflow: auto; }
</style>
